// apps/dashboard/src/hooks/use-macros.ts
import { api } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export type MacroActionType =
  | 'send_reply'
  | 'add_note'
  | 'add_label'
  | 'remove_label'
  | 'assign_agent'
  | 'assign_inbox'
  | 'set_priority'
  | 'resolve_conversation';

export type MacroVisibility = 'private' | 'team' | 'global';

export interface MacroActionDto {
  type: MacroActionType;
  // action-specific fields (content, label, agentId, inboxId, priority)
  [key: string]: unknown;
}

export interface MacroDto {
  id: string;
  tenantId: string;
  createdBy: string;
  name: string;
  description?: string | null;
  visibility: MacroVisibility;
  actions: MacroActionDto[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateMacroDto {
  name: string;
  description?: string;
  visibility: MacroVisibility;
  actions: MacroActionDto[];
}

export function useMacros(params?: { visibility?: string; search?: string }) {
  return useQuery({
    queryKey: ['macros', params],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (params?.visibility) qs.set('visibility', params.visibility);
      if (params?.search) qs.set('search', params.search);
      return api.get<{ macros: MacroDto[] }>(`/me/macros?${qs}`);
    },
  });
}

export function useCreateMacro() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateMacroDto) => api.post<MacroDto>('/me/macros', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['macros'] });
    },
  });
}

export function useUpdateMacro() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<CreateMacroDto> & { id: string }) =>
      api.put<MacroDto>(`/me/macros/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['macros'] });
    },
  });
}

export function useDeleteMacro() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/me/macros/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['macros'] });
    },
  });
}

export function useExecuteMacro() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ macroId, conversationId }: { macroId: string; conversationId: string }) =>
      api.post(`/me/macros/${macroId}/execute`, { conversationId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}
